import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Truck, ShieldCheck, RotateCcw, Percent, Quote } from "lucide-react";
import api, { gbp } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { BLOG_POSTS } from "@/data/blog";
import { fmtDate } from "@/lib/seo";

const disciplines = [
  { name: "Carp Fishing", tag: "The Furn speciality", img: "https://images.unsplash.com/photo-1633251136226-d8b4ca5456cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", span: "lg:col-span-8 lg:row-span-2" },
  { name: "Coarse Fishing", tag: "Match & pleasure", img: "https://images.unsplash.com/photo-1496628042519-1ff9922747bd?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", span: "lg:col-span-4" },
  { name: "Sea Fishing", tag: "Beach & boat", img: "https://images.unsplash.com/photo-1529559992548-a89cf75a13b2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", span: "lg:col-span-4" },
];

const brands = ["Fox", "Nash", "Korda", "Shimano", "Daiwa", "Mainline", "Sticky Baits", "Trakker", "ESP", "RidgeMonkey", "Sonik", "Avid Carp"];

const testimonials = [
  { name: "Liam D.", text: "Fast delivery and genuinely knowledgeable staff. My go-to for all my carp gear now.", rating: 5 },
  { name: "Chris B.", text: "Ordered a bivvy on Tuesday, fishing under it Thursday night. Superb service from Furn.", rating: 5 },
  { name: "Ade M.", text: "Prices are spot on and the loyalty points soon add up. Highly recommend these guys.", rating: 5 },
];

const Section = ({ title, kicker, link, children }) => (
  <section className="container-furn py-14">
    <div className="mb-8 flex items-end justify-between">
      <div>
        {kicker && <span className="overline">{kicker}</span>}
        <h2 className="mt-1 font-display text-3xl md:text-4xl font-semibold uppercase tracking-tight">{title}</h2>
      </div>
      {link && <Link to={link} className="hidden shrink-0 items-center gap-1 text-xs font-bold uppercase tracking-widest text-accent hover:text-foreground transition-colors sm:flex">View all <ArrowRight className="h-4 w-4" /></Link>}
    </div>
    {children}
  </section>
);

export default function Home() {
  const [featured, setFeatured] = useState({ new_arrivals: [], best_sellers: [], staff_picks: [], offers: [] });
  const [bundles, setBundles] = useState([]);
  const [season, setSeason] = useState(null);
  const [blog, setBlog] = useState([]);

  useEffect(() => {
    api.get("/products/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/bundles").then((r) => setBundles(r.data)).catch(() => {});
    api.get("/season").then((r) => setSeason(r.data)).catch(() => {});
    api.get("/blog").then((r) => setBlog(r.data.posts)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative flex min-h-[88vh] items-center">
        <img src="https://images.unsplash.com/photo-1633251136226-d8b4ca5456cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="Dawn over a carp lake" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-background/20" />
        <div className="container-furn relative">
          <div className="max-w-2xl animate-fade-up">
            <span className="overline">Specialist Fishing Tackle · Est. Essex</span>
            <h1 className="mt-4 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight md:text-7xl">
              Gear up for<br /><span className="text-accent">the take.</span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-foreground/80 md:text-lg">
              Premium carp, coarse, predator and sea fishing tackle from the brands you trust. Hand-picked, keenly priced and ready for the bank.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/shop?discipline=Carp%20Fishing" data-testid="hero-shop-carp" className="group flex items-center gap-2 bg-accent px-8 py-4 text-xs font-bold uppercase tracking-widest text-accent-foreground hover:brightness-110 transition-[filter]">
                Shop Carp <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/shop" data-testid="hero-shop-all" className="border border-foreground/30 px-8 py-4 text-xs font-bold uppercase tracking-widest hover:border-accent hover:text-accent transition-colors">
                Shop All Tackle
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <div className="border-y border-border bg-card">
        <div className="container-furn grid grid-cols-2 gap-4 py-6 text-center md:grid-cols-4">
          {[[Truck, "Free delivery over £50"], [ShieldCheck, "Secure checkout"], [RotateCcw, "30-day returns"], [Percent, "Klarna & Clearpay"]].map(([Icon, t]) => (
            <div key={t} className="flex items-center justify-center gap-2.5 text-sm">
              <Icon className="h-5 w-5 text-accent" /><span className="font-medium">{t}</span>
            </div>
          ))}
        </div>
      </div>

      {/* FEATURED CATEGORIES */}
      <Section title="Shop by Discipline" kicker="Find your water">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
          {disciplines.map((d, i) => (
            <Link key={d.name} to={`/shop?discipline=${encodeURIComponent(d.name)}`} data-testid={`category-tile-${i}`} className={`group relative flex min-h-[240px] items-end overflow-hidden border border-border ${d.span} ${i === 0 ? "md:col-span-8 md:row-span-2 md:min-h-[500px]" : "md:col-span-4 md:min-h-[240px]"}`}>
              <img src={d.img} alt={d.name} className="zoom-img absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
              <div className="relative p-6">
                <span className="overline">{d.tag}</span>
                <h3 className="mt-1 font-display text-2xl font-semibold uppercase md:text-3xl">{d.name}</h3>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-accent">Shop now <ArrowRight className="h-3.5 w-3.5" /></span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* SEASON PICKS */}
      {season && season.products.length > 0 && (
        <section className="border-y border-border bg-card py-14" data-testid="season-section">
          <div className="container-furn">
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 overline"><span className="inline-block h-2 w-2 animate-pulse rounded-full bg-accent" /> What's working now · {season.season}</span>
                <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold uppercase tracking-tight" data-testid="season-title">{season.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground" data-testid="season-tagline">{season.tagline}</p>
              </div>
              <Link to="/shop" className="hidden shrink-0 items-center gap-1 text-xs font-bold uppercase tracking-widest text-accent hover:text-foreground transition-colors sm:flex">Shop the season <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {season.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* BRANDS */}
      <section className="border-y border-border bg-card py-10">
        <div className="container-furn">
          <p className="mb-6 text-center overline">Trusted brands, all in one place</p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {brands.map((b) => (
              <Link key={b} to={`/shop?brand=${encodeURIComponent(b)}`} className="font-display text-lg font-semibold uppercase tracking-wide text-muted-foreground hover:text-accent transition-colors">{b}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      {featured.new_arrivals.length > 0 && (
        <Section title="New Arrivals" kicker="Just landed" link="/shop?is_new=true">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {featured.new_arrivals.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </Section>
      )}

      {/* SEASONAL OFFER BANNER */}
      <section className="container-furn py-6">
        <div className="relative flex min-h-[320px] items-center overflow-hidden border border-border">
          <img src="https://images.unsplash.com/photo-1632862316541-0b75b0126544?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Angler at dusk" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-background/40" />
          <div className="relative p-10 md:p-14">
            <span className="overline text-white">Seasonal Clearance</span>
            <h2 className="mt-2 max-w-md font-display text-4xl font-bold uppercase leading-tight md:text-5xl">Up to 30% off end of line gear</h2>
            <Link to="/shop?on_offer=true" className="mt-6 inline-flex items-center gap-2 bg-white px-8 py-4 text-xs font-bold uppercase tracking-widest text-background hover:bg-accent transition-colors">
              Shop the sale <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BEST SELLERS */}
      {featured.best_sellers.length > 0 && (
        <Section title="Best Sellers" kicker="Bank-side favourites" link="/shop">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {featured.best_sellers.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </Section>
      )}

      {/* BUNDLES */}
      {bundles.length > 0 && (
        <Section title="Deals & Bundles" kicker="Save more, catch more" link="/bundles">
          <div className="grid gap-5 md:grid-cols-3">
            {bundles.slice(0, 3).map((b, i) => (
              <Link key={b.id} to="/bundles" data-testid={`home-bundle-${i}`} className="group flex flex-col overflow-hidden border border-border bg-card transition-colors hover:border-primary animate-fade-up" style={{ animationDelay: `${i * 70}ms` }}>
                <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
                  <img src={b.image} alt={b.name} className="zoom-img h-full w-full object-cover" />
                  <span className="absolute left-0 top-3 bg-destructive px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">{b.badge}</span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-display text-lg font-semibold uppercase leading-tight group-hover:text-accent transition-colors">{b.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{b.tagline}</p>
                  <div className="mt-auto flex items-baseline gap-2 pt-4">
                    <span className="font-display text-xl font-bold">{gbp(b.bundle_price)}</span>
                    <span className="text-sm text-muted-foreground line-through">{gbp(b.original_price)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* STAFF PICKS */}
      {featured.staff_picks.length > 0 && (
        <Section title="Staff Picks" kicker="Tried & tested by the team" link="/shop">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {featured.staff_picks.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </Section>
      )}

      {/* TESTIMONIALS */}
      <section className="border-y border-border bg-card py-16">
        <div className="container-furn">
          <div className="mb-10 text-center">
            <span className="overline">Rated excellent by anglers</span>
            <h2 className="mt-1 font-display text-3xl md:text-4xl font-semibold uppercase">What the bank says</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <div key={t.name} className="border border-border bg-background p-7">
                <Quote className="h-7 w-7 text-accent" />
                <div className="mt-3 flex gap-0.5">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-accent text-accent" />)}</div>
                <p className="mt-3 text-sm leading-relaxed text-foreground/80">"{t.text}"</p>
                <p className="mt-4 text-sm font-semibold">{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG */}
      <Section title="Fishing Guides" kicker="From the bank" link="/blog">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {(blog.length ? blog : BLOG_POSTS).slice(0, 4).map((post) => (
            <Link key={post.slug} to={`/blog/${post.slug}`} className="group border border-border bg-card transition-colors hover:border-primary">
              <div className="aspect-[4/3] overflow-hidden bg-secondary">
                <img src={post.image} alt={post.title} className="zoom-img h-full w-full object-cover" />
              </div>
              <div className="p-5">
                <span className="overline">{post.category}</span>
                <h3 className="mt-2 font-display text-lg font-medium leading-snug group-hover:text-accent transition-colors line-clamp-2">{post.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{post.read} read · {fmtDate(post.date)}</p>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* INSTAGRAM */}
      <section className="container-furn pb-16">
        <div className="mb-6 text-center">
          <span className="overline">@furnangling</span>
          <h2 className="mt-1 font-display text-2xl md:text-3xl font-semibold uppercase">Follow the catches</h2>
        </div>
        <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
          {["https://images.unsplash.com/photo-1570655371646-1108a3515435","https://images.unsplash.com/photo-1633251136226-d8b4ca5456cd","https://images.unsplash.com/photo-1778016740380-21c3277768f2","https://images.unsplash.com/photo-1529559992548-a89cf75a13b2","https://images.unsplash.com/photo-1496628042519-1ff9922747bd","https://images.unsplash.com/photo-1632862316541-0b75b0126544"].map((src, i) => (
            <a key={i} href="#" className="group relative aspect-square overflow-hidden bg-secondary">
              <img src={`${src}?crop=entropy&cs=srgb&fm=jpg&q=80&w=500`} alt="Instagram" className="zoom-img h-full w-full object-cover" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
